import {
  Delete,
  XY as IconXY,
  NumberLineAdd,
  NumberLineBoxOne,
  NumberLineOne,
  Reset,
  See,
} from "assets/icons/Common";
import { IconButtonInverted, Row } from "components/primitives";
import { lerp } from "components/utils";
import { ModalsContext } from "contexts/ModalsContext";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Vector2 } from "types/Vector2";
import { WidgetFC } from "types/Widget";

import {
  Denominators,
  MaxVisibleSegments,
  MODAL_NEW_NUMBER_SPAN,
  padding,
  StepSettings,
} from "./constants";
import { NumberSettingData } from "./interfaces";
import { ModalNumberSpan } from "./ModalNumberSpan";
import { NumberLineFraction } from "./NumberLineFraction";
import { NumberLineSpan } from "./NumberLineSpan";
import { numberToFraction, roundToFraction } from "./numberToFraction";

export interface NumberLineState {
  originalSize?: Size;
  spans: SpanData[];
  hideNumbers: boolean;
}

export interface Size {
  width: number;
  height: number;
}

export interface SpanData {
  startNumber: number;
  endNumber: number;
  yPosition: number;
  showLength: boolean;
  showEnds: boolean;
  hiddenNumbers: { [position: string]: boolean };
}

export const NumberLine: WidgetFC<NumberLineState> = ({
  state,
  setState,
  settings,
  pixelPosition,
}) => {
  const { stepSize, interval } = settings;
  const { spans, originalSize, hideNumbers } = state;
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | undefined>(undefined);

  const modalsContext = useContext(ModalsContext);
  const container = useRef<HTMLDivElement>(null);

  const safeStartNumber = Math.min(interval.startNumber, interval.endNumber);
  const safeEndNumber = Math.max(interval.startNumber, interval.endNumber);

  const stepSetting = StepSettings[stepSize];
  const scaledPadding = padding * stepSetting;
  const numberLineWidth = Math.max(
    ((safeEndNumber - safeStartNumber + scaledPadding * 2 + 2 * stepSetting) /
      stepSetting /
      (MaxVisibleSegments + 1)) *
      100,
    100,
  );
  const fullLineWidth = (pixelPosition.width || 0) * (numberLineWidth / 100);

  useEffect(() => {
    if (
      originalSize === undefined &&
      (pixelPosition.width || 1) > 1 &&
      (pixelPosition.width || 1) > 1
    ) {
      setState({ originalSize: { width: pixelPosition.width!, height: pixelPosition.height! } });
    }
  }, [pixelPosition.width, pixelPosition.height, originalSize]);

  useEffect(() => {
    const newSpans = [];
    for (let i = 0; i < spans.length; i++) {
      let roundedSpan: SpanData = {
        ...spans[i],
        startNumber: roundToFraction(spans[i].startNumber, StepSettings[stepSize]),
        endNumber: roundToFraction(spans[i].endNumber, StepSettings[stepSize]),
      };
      if (roundedSpan.startNumber >= roundedSpan.endNumber) {
        roundedSpan.endNumber++;
      }
      if (
        roundedSpan.startNumber >= interval.startNumber &&
        roundedSpan.endNumber <= interval.endNumber
      ) {
        newSpans.push(roundedSpan);
      }
    }
    setState({ spans: newSpans });
  }, [stepSize, interval]);

  const scale: Vector2 = useMemo(() => {
    return {
      x: (pixelPosition.width || 1) / (originalSize?.width || 1),
      y: (pixelPosition.height || 1) / (originalSize?.height || 1),
    };
  }, [originalSize, pixelPosition.width, pixelPosition.height]);

  const addSpan = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const newSpans = [...spans];
    const freeLanes = [0, 1, 2, 3, 4, 5, 6].filter(
      (lane) =>
        !newSpans.some((span) => span.yPosition === lane && span.startNumber === safeStartNumber),
    );
    const freeLane = freeLanes.length > 0 ? freeLanes[0] : 0;
    newSpans.push({
      startNumber: safeStartNumber,
      endNumber: safeStartNumber + 5 * stepSetting,
      yPosition: freeLane,
      showEnds: true,
      showLength: true,
      hiddenNumbers: { left: hideNumbers, center: hideNumbers, right: hideNumbers },
    });
    setState({ spans: newSpans });
  };

  const deleteSpan = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.stopPropagation();
    const newSpans = [...spans];
    newSpans.splice(index, 1);
    setState({ spans: newSpans });
    setSelectedIndex(undefined);
  };

  const deleteAllSpans = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setState({ spans: [] });
  };

  const toggleHideNumbers = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!hideNumbers) {
      const newSpans = [...spans];
      for (let i = 0; i < newSpans.length; i++) {
        newSpans[i].hiddenNumbers = { left: true, center: true, right: true };
      }
      setState({ spans: newSpans });
    }
    setState({ hideNumbers: !hideNumbers });
  };

  const toggleHideEnds = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.stopPropagation();
    if (index < spans.length) {
      const newSpans = [...spans];
      newSpans[index].showEnds = !newSpans[index].showEnds;
      setState({ spans: newSpans });
    }
  };

  const toggleHideLength = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.stopPropagation();
    if (index < spans.length) {
      const newSpans = [...spans];
      newSpans[index].showLength = !newSpans[index].showLength;
      setState({ spans: newSpans });
    }
  };

  const onSpanDataChanged = (index: number, data: Partial<SpanData>) => {
    if (animatedSteps.length) {
      return;
    }

    const newSpans = spans.map((span, spanIndex) => {
      if (spanIndex === index) {
        return {
          ...span,
          ...data,
        };
      }

      return span;
    });

    setState({ spans: newSpans });
  };

  const selectSpan = (index: number | undefined) => {
    if (animatedSteps.length) {
      return;
    }

    setSelectedIndex(index);
    if (index !== undefined) {
      setLastSelectedIndex(index);
    }
  };

  const selectHiddenNumber = (index: number, position: string) => {
    const data = spans[index];
    const newSpans = [...spans];

    data.hiddenNumbers[position] = !data.hiddenNumbers[position];
    newSpans[index] = data;

    setState({ spans: newSpans });
  };

  const getFontSize = (number: number) => {
    if (stepSize === "Whole Numbers") {
      return 3;
    } else if (stepSize === "Decimals (Tenths)") {
      let size = lerp(3, 1.5, Math.abs(number) / 1000);
      if (number < -10) {
        size /= 1.25;
      }
      return size;
    } else {
      if (number >= 100 || number <= -100) {
        return 2.5;
      } else {
        return 3;
      }
    }
  };

  const getDisplayNumber = (number: number, fontSize: number) => {
    if (stepSize === "Whole Numbers") {
      return <p style={{ fontSize: `${fontSize}cqh` }}>{number}</p>;
    } else if (stepSize === "Decimals (Tenths)") {
      return <p style={{ fontSize: `${fontSize}cqh` }}>{number.toFixed(1)}</p>;
    } else {
      return (
        <NumberLineFraction
          fontSize={fontSize}
          {...numberToFraction(number, Denominators[stepSize])}
        />
      );
    }
  };

  const getNotchNumber = (index: number) => {
    return safeStartNumber + index * stepSetting - scaledPadding;
  };

  const notchCount = Math.round(
    (safeEndNumber - safeStartNumber + stepSetting - scaledPadding) / stepSetting + padding,
  );

  const handleOpenModalNewNumberSpan = () => {
    modalsContext.openModal({
      name: MODAL_NEW_NUMBER_SPAN,
    });
  };

  const [animatedSteps, setAnimatedSteps] = useState<number[]>([]);
  const [animatedSpanIndex, setAnimatedSpanIndex] = useState<number>();

  useEffect(() => {
    const [first, ...rest] = animatedSteps;

    if (!animatedSteps.length) {
      return;
    }

    const animation = setTimeout(() => {
      if (!animatedSteps.length) {
        return;
      }

      const newSpans = spans.map((span, spanIndex) => {
        if (spanIndex === spans.length - 1) {
          if (Math.sign(first) === 1) {
            return {
              ...span,
              endNumber: span.endNumber + first,
            };
          } else {
            return {
              ...span,
              startNumber: span.startNumber + first,
            };
          }
        }

        return span;
      });

      setState({ spans: newSpans });
      setAnimatedSteps(rest);
    }, 400);

    return () => {
      if (!animatedSteps.length) {
        clearInterval(animation);
        setAnimatedSpanIndex(undefined);
      }
    };
  }, [animatedSteps]);

  const handleSetNewSpan = (spanSetting: NumberSettingData, steps: number[]) => {
    const freeLanes = [0, 1, 2, 3, 4, 5, 6].filter(
      (lane) => !spans.some((span) => span.yPosition === lane),
    );

    const freeLane = freeLanes.length > 0 ? freeLanes[0] : 0;
    const newSpans = spans.concat({
      startNumber: +spanSetting.startNumber,
      endNumber: +spanSetting.startNumber,
      yPosition: freeLane,
      showEnds: true,
      showLength: true,
      hiddenNumbers: { left: hideNumbers, center: hideNumbers, right: hideNumbers },
    });

    setState({ spans: newSpans });
    setAnimatedSpanIndex(newSpans.length - 1);
    setAnimatedSteps(steps);
  };

  return (
    <OuterContainer ref={container}>
      <NumberLineScroll>
        <NumberLineArea
          style={{ width: `${numberLineWidth}%` }}
          onClick={() => selectSpan(undefined)}
        >
          <NumberLineBackground />
          <div style={{ padding: "2cqh 0px" }}>
            {spans.map((span, index) => (
              <NumberLineSpan
                key={index}
                // add id
                index={index}
                scale={scale}
                widgetSize={{ width: pixelPosition.width || 1, height: pixelPosition.height || 1 }}
                maxVisibleSegments={MaxVisibleSegments}
                fullWidth={fullLineWidth}
                startNumber={safeStartNumber}
                hideNumbers={hideNumbers}
                padding={padding}
                data={span}
                onDataChanged={onSpanDataChanged.bind(null, index)}
                isSelected={selectedIndex === index}
                showInFront={lastSelectedIndex === index}
                onSelect={selectSpan.bind(null, index)}
                stepSetting={stepSetting}
                onSelectHiddenNumber={selectHiddenNumber.bind(null, index)}
                animatedSteps={animatedSteps}
                animatedSpanIndex={animatedSpanIndex}
              />
            ))}
          </div>
          <NumberLineDigits>
            <svg viewBox={`0 0 ${fullLineWidth} ${20 * scale.y}`}>
              <line
                x1="0"
                y1={10 * scale.y}
                x2={fullLineWidth}
                y2={10 * scale.y}
                stroke="var(--widget-primary)"
                strokeWidth={4 * scale.x}
              />
              {[...Array(notchCount)].map((e, i) => (
                <line
                  key={i}
                  x1={((pixelPosition.width! + 2) / (MaxVisibleSegments + 1)) * (i + 1 + padding)}
                  y1={3}
                  x2={((pixelPosition.width! + 2) / (MaxVisibleSegments + 1)) * (i + 1 + padding)}
                  y2={17 * scale.y}
                  stroke="var(--widget-primary)"
                  strokeWidth={4 * scale.x}
                  strokeLinecap="round"
                />
              ))}
            </svg>
            {[...Array(notchCount + padding)].map((e, i) => {
              const currentNumber =
                getNotchNumber(i) >= safeStartNumber &&
                getNotchNumber(i) <= safeEndNumber &&
                getDisplayNumber(getNotchNumber(i), getFontSize(safeStartNumber + i * stepSetting));

              return (
                <NumberLineDigit
                  key={i}
                  style={{
                    left: (pixelPosition.width! / (MaxVisibleSegments + 1)) * (i + 1),
                  }}
                >
                  {currentNumber !== false && currentNumber}
                </NumberLineDigit>
              );
            })}
          </NumberLineDigits>
        </NumberLineArea>
      </NumberLineScroll>
      <ControlsContainer onClick={() => selectSpan(undefined)}>
        {selectedIndex === undefined || selectedIndex >= spans.length ? (
          <>
            <IconButtonInverted Icon={NumberLineAdd} withShadow={false} onClick={addSpan} />

            <IconButtonInverted
              Icon={IconXY}
              withShadow={false}
              onClick={handleOpenModalNewNumberSpan}
            />

            {hideNumbers ? (
              <IconButtonInverted
                accentColor="var(--green)"
                Icon={See}
                withShadow={false}
                onClick={toggleHideNumbers}
              />
            ) : (
              <IconButtonInverted Icon={See} withShadow={false} onClick={toggleHideNumbers} />
            )}

            <IconButtonInverted
              Icon={Reset}
              withShadow={false}
              accentColor="var(--danger)"
              onClick={deleteAllSpans}
            />
          </>
        ) : (
          <>
            {spans[selectedIndex].showLength ? (
              <IconButtonInverted
                Icon={NumberLineOne}
                withShadow={false}
                onClick={(e) => toggleHideLength(e, selectedIndex)}
              />
            ) : (
              <IconButtonInverted
                accentColor="var(--green)"
                Icon={NumberLineOne}
                withShadow={false}
                onClick={(e) => toggleHideLength(e, selectedIndex)}
              />
            )}

            {spans[selectedIndex].showEnds ? (
              <IconButtonInverted
                Icon={NumberLineBoxOne}
                withShadow={false}
                onClick={(e) => toggleHideEnds(e, selectedIndex)}
              />
            ) : (
              <IconButtonInverted
                accentColor="var(--green)"
                Icon={NumberLineBoxOne}
                withShadow={false}
                onClick={(e) => toggleHideEnds(e, selectedIndex)}
              />
            )}

            <IconButtonInverted
              Icon={Delete}
              withShadow={false}
              accentColor="var(--danger)"
              onClick={(e) => deleteSpan(e, selectedIndex)}
            />
          </>
        )}
      </ControlsContainer>

      <ModalNumberSpan
        onSetSpanSettings={handleSetNewSpan}
        container={container}
        config={{ stepSize, interval }}
      />
    </OuterContainer>
  );
};

const OuterContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const ControlsContainer = styled(Row)`
  height: 15%;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 1cqw;

  button {
    height: 50%;
    aspect-ratio: 1;
  }
`;

const NumberLineScroll = styled.div.attrs({ className: "no-drag" })`
  width: 100%;
  height: 85%;
  border-bottom: 1px solid black;
  overflow-x: auto;
  position: relative;
`;

const NumberLineArea = styled.div`
  height: 100%;
  position: relative;

  .react-draggable {
    position: absolute;
  }
`;

const NumberLineBackground = styled.div`
  position: absolute;
  top: 0;
  left: ${(2 * 100) / (MaxVisibleSegments + 1)}cqw;
  width: calc(100% - ${(3 * 100) / (MaxVisibleSegments + 1)}cqw);
  height: 100%;
  background-image: none, repeating-linear-gradient(90deg, #ccc 0 1px, transparent 1px 100%);
  background-size: ${100 / (MaxVisibleSegments + 1)}cqw ${100 / (MaxVisibleSegments + 1)}cqw;
`;

const NumberLineDigits = styled.div`
  position: absolute;
  width: 100%;
  height: 13cqh;
  bottom: 0;
  left: 0;
  background-color: white;
  transform: translate(0, 0);
`;

const NumberLineDigit = styled.div`
  position: absolute;
  transform: translate(-50%, 0%);
  line-height: 1em;
  font-weight: 600;
  top: 50%;
`;
